import './TemplateEditorBox.scss'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Col } from 'react-bootstrap'
import { $generateHtmlFromNodes } from '@lexical/html'
import { EditorState, LexicalEditor } from 'lexical'
import { RichTextToolbarPlugin } from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/Plugins/RichTextToolbarPlugin/RichTextToolbarPlugin'
import { LEXICAL_NODES } from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/nodes'
import { WritableLexicalEditor } from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/WritableLexicalEditor/WritableLexicalEditor'
import { getEditorStateFromHtml } from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/utils'
import { Button } from '../../../components/Button/Button'
import { TemplateValue } from '../../../models/TemplateLibrary'
import { useAppSelector } from '../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { useBlocker } from 'react-router-dom'
import { YesNoCancelModal } from '../../../components/Modal/YesNoCancelModal'
import { useAccount } from '../../../common/useAccount'

export interface TemplateEditorBoxProps {
  templateValue: TemplateValue
  shouldReset: boolean
  readOnly?: boolean
  onSave?: (newTemplate: string) => void
  loading?: boolean
}

export const TemplateEditorBox: React.FC<TemplateEditorBoxProps> = ({ templateValue, shouldReset, readOnly, onSave, loading }) => {
  const { account } = useAccount()
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const {
    template: { template },
  } = templateValue
  const editorRef = useRef<LexicalEditor>(null)
  const [currentHtml, setCurrentHtml] = useState<string>(template)
  const [isDirty, setIsDirty] = useState<boolean>(false)
  const blocker = useBlocker(useCallback(() => isDirty, [isDirty]))
  const [showBlockerModal, setShowBlockerModal] = useState<boolean>(false)
  const normalizedHtmlRef = useRef<string | null>(null)
  const isTemplateOwnedByAnotherUser = templateValue.scope == 'user' && 'createdBy' in templateValue && templateValue.createdBy !== currentUserId
  const templatesLocked = account?.enterpriseConfig.lockTemplates ?? false
  const disabledBannerText = templatesLocked
    ? 'Templates are managed at the enterprise level. You can’t edit or duplicate them.'
    : templateValue.scope == 'system'
      ? 'This is a system template maintained by Zocks. You can’t edit it. Duplicate it to create your own version.'
      : 'This is an enterprise template. You can’t edit it. Duplicate it to create your own version.'

  const [initialEditorState, setInitialEditorState] = useState<EditorState>(() => {
    return getEditorStateFromHtml(template)
  })

  useEffect(() => {
    setInitialEditorState(getEditorStateFromHtml(template))
    normalizedHtmlRef.current = null
  }, [template])

  const resetToInitialTemplate = useCallback(() => {
    const editor = editorRef.current
    if (!editor) {
      return
    }
    editor.setEditorState(initialEditorState)
  }, [initialEditorState])

  useEffect(() => {
    if (shouldReset) {
      resetToInitialTemplate()
    }
  }, [shouldReset, resetToInitialTemplate])

  useEffect(() => {
    resetToInitialTemplate()
  }, [resetToInitialTemplate])

  useEffect(() => {
    editorRef.current?.setEditable(!readOnly)
  }, [readOnly])

  const handleDiscard = useCallback(() => {
    resetToInitialTemplate()
  }, [resetToInitialTemplate])

  const handleChange = () => {
    const editor = editorRef.current
    if (!editor) {
      return
    }
    const editorState = editor.getEditorState()
    editorState.read(() => {
      const newHtml = $generateHtmlFromNodes(editor, null)

      // On first change after loading, capture the normalized HTML from Lexical
      // This prevents false positives from Lexical's automatic attribute additions
      if (normalizedHtmlRef.current === null) {
        normalizedHtmlRef.current = newHtml
      }

      setCurrentHtml(newHtml)
    })
  }

  useEffect(() => {
    // Compare against the normalized HTML to avoid false positives from Lexical's auto-normalization
    const baselineHtml = normalizedHtmlRef.current ?? template
    setIsDirty(!readOnly && currentHtml !== baselineHtml)
  }, [currentHtml, template, readOnly, setIsDirty])

  useEffect(() => {
    setShowBlockerModal(blocker.state === 'blocked')
  }, [blocker.state])

  const proceedBlocker = (save: boolean) => {
    if (save) {
      onSave?.(currentHtml)
    }
    setShowBlockerModal(false)
    if (blocker.state === 'blocked') {
      blocker.proceed()
    }
  }

  const resetBlocker = () => {
    if (blocker.state === 'blocked') {
      blocker.reset()
      setShowBlockerModal(false)
    }
  }

  const handleSave = useCallback(() => {
    if (onSave) {
      onSave(currentHtml)
    }
  }, [onSave, currentHtml])

  return (
    <>
      <Col className='d-flex flex-column h-100'>
        <div className='template-library-box d-flex flex-column flex-grow-1 h-100'>
          <div className='template-library-dynamic-panel d-flex flex-column flex-grow-1 overflow-hidden'>
            {initialEditorState ? (
              <WritableLexicalEditor
                className='zocks-px-2.0 zocks-py-2.0 template-library-box-editor flex-grow-1 d-flex flex-column overflow-hidden'
                containerClassName='w-100'
                editorContainerClassName='template-library-editor flex-grow-1 px-4 py-3 overflow-y-auto'
                onChange={handleChange}
                initialEditorState={initialEditorState}
                lexicalNodes={LEXICAL_NODES}
                header={
                  !readOnly ? (
                    <RichTextToolbarPlugin className='p-3 gap-2 border border-1 zocks-border-1.0 zocks-border-color-gray-300' />
                  ) : (
                    <TemplateEditorDisabledBanner text={disabledBannerText} />
                  )
                }
                ref={editorRef}
              />
            ) : null}
          </div>

          {!readOnly ? (
            <TemplateEditorBoxControls
              onDiscard={handleDiscard}
              onSave={handleSave}
              loading={Boolean(loading)}
              infoText={isTemplateOwnedByAnotherUser ? 'This is a template owned by a user.' : undefined}
            />
          ) : null}
        </div>
      </Col>
      {showBlockerModal && (
        <YesNoCancelModal
          show={showBlockerModal}
          onHide={resetBlocker}
          message={'You have unsaved changes. Do you want to save your changes?'}
          onYes={() => proceedBlocker(true)}
          onNo={() => proceedBlocker(false)}
        />
      )}
    </>
  )
}

interface TemplateEditorBoxControlsProps {
  onDiscard: () => void
  onSave: () => void
  loading: boolean
  infoText?: string
}

const TemplateEditorBoxControls: React.FC<TemplateEditorBoxControlsProps> = ({ onDiscard, onSave, infoText, loading }) => {
  return (
    <div className='template-editor-box-controls position-sticky start-0 end-0 py-3 zocks-px-1.75 rounded-4 shadow d-flex justify-content-between align-items-center gap-2'>
      {infoText ? (
        <div className='d-flex align-items-center gap-2'>
          <i className='bi bi-info-square zocks-text-gray-500' />
          <span className='zocks-text-gray-500'>{infoText}</span>
        </div>
      ) : (
        <div />
      )}
      <div className='d-flex gap-2 justify-content-end'>
        <Button variant='darkGhost' onClick={onDiscard}>
          Discard
        </Button>
        <Button variant='primary' onClick={onSave} loading={loading}>
          Save
        </Button>
      </div>
    </div>
  )
}

interface TemplateEditorDisabledBannerProps {
  text: string
}

const TemplateEditorDisabledBanner: React.FC<TemplateEditorDisabledBannerProps> = ({ text }) => {
  return (
    <div className='d-flex align-items-center gap-2 p-3 rounded-4 border zocks-border-color-gray-300 zocks-bg-color-gray-100'>
      <i className='bi bi-info-square zocks-text-gray-900' />
      <span className='zocks-text-gray-900'>{text}</span>
    </div>
  )
}
